import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"
import { retoneStatusBadge } from "../lib/status_badge"

// The header Support Status pill (#19912): a Support Staff member sets
// themselves Available or Unavailable. The pill is the trigger of the shared
// menu (gl_more_menu, whose more-menu controller opens, closes and runs the
// keyboard); a pick arrives as `more-menu:select` with the row's value. This
// saves it through PATCH /api/v1/react/support_status, then swaps the pill's
// tone and label and moves the check to the new choice.
// Markup: layouts/goatleads/_support_status.html.haml.
const CHOICE_SELECTOR = '[role="menuitemradio"]'
const CHECK_MARK_CLASSES = ["ph", "ph-check", "gl-dropdown-item-end"] // MoreMenuItem's `checked` mark

export default class extends Controller {
  static targets = ["pill", "label"]
  static values = {
    url: String,
    available: Boolean,
    availableLabel: String,
    unavailableLabel: String,
    availableTone: String,
    unavailableTone: String,
  }

  connect() {
    this.saving = false
  }

  async choose(event) {
    const available = event.detail.value === "true"
    if (this.saving || available === this.availableValue) return

    this.saving = true
    try {
      const response = await patch(this.urlValue, { body: { available }, responseKind: "json" })
      if (!response.ok) {
        const data = await response.json.catch(() => ({}))
        window.showToast?.(data.errors?.[0] || "Could not save your support status", "danger")
        return
      }
      this.availableValue = available
      this.#render()
    } finally {
      this.saving = false
    }
  }

  #render() {
    const on = this.availableValue
    const label = on ? this.availableLabelValue : this.unavailableLabelValue
    retoneStatusBadge(this.pillTarget, on ? this.availableToneValue : this.unavailableToneValue)
    this.pillTarget.setAttribute("aria-label", label)
    if (this.pillTarget.dataset.glTooltip) this.pillTarget.dataset.glTooltip = label
    if (this.hasLabelTarget) this.labelTarget.textContent = label
    this.element.querySelectorAll(CHOICE_SELECTOR).forEach((choice) => {
      this.#setChecked(choice, choice.dataset.moreMenuValue === String(on))
    })
  }

  // Drawn as MoreMenuItem draws `checked`: aria-checked, and the check mark after the text.
  #setChecked(choice, checked) {
    choice.setAttribute("aria-checked", String(checked))
    const mark = choice.querySelector(`.${CHECK_MARK_CLASSES.join(".")}`)
    if (!checked) {
      mark?.remove()
    } else if (!mark) {
      const icon = document.createElement("i")
      icon.className = CHECK_MARK_CLASSES.join(" ")
      icon.setAttribute("aria-hidden", "true")
      choice.querySelector(".gl-dropdown-item-text").after(icon)
    }
  }
}
